import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveFooter,
  ResponsiveHeader,
} from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { BankIcon, CreditCardIcon, DocumentsIcon } from '../ui/Icons';
import Modal from '../ui/Modal';
import FileUpload from '../ui/FileUpload';
import ManualContactModal from '../ui/ManualContactModal';
import type { UnifiedContact } from '../../services/unifiedContactService';
import { unifiedContactService } from '../../services/unifiedContactService';
import {
  autoSyncDistributionForAsset,
  removeAssetDistribution,
  type DistributionMap,
} from '../../utils/distributionUtils';
import { SubHeadline } from '../ui/Typography';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

interface Beneficiary {
  contactId: string;
  name: string;
}

interface Account {
  id: string;
  type: 'checking' | 'savings' | 'money_market' | 'cd' | 'other';
  name: string;
  institution: string;
  accountNumber: string;
  routingNumber?: string;
  balance?: number;
  interestRate?: number;
  maturityDate?: string;
  beneficiaries?: Beneficiary[];
  notes?: string;
  documents?: AppDocument[];
}

const ACCOUNT_TYPES = [
  {
    key: 'checking' as const,
    label: 'Checking Accounts',
    description: 'Primary transaction accounts for daily expenses',
    Icon: BankIcon,
  },
  {
    key: 'savings' as const,
    label: 'Savings Accounts',
    description: 'Interest-bearing accounts for building savings',
    Icon: BankIcon,
  },
  {
    key: 'money_market' as const,
    label: 'Money Market Accounts',
    description: 'Higher-yield accounts with check-writing privileges',
    Icon: CreditCardIcon,
  },
  {
    key: 'cd' as const,
    label: 'Certificates of Deposit',
    description: 'Fixed-term deposits with guaranteed returns',
    Icon: BankIcon,
  },
  {
    key: 'other' as const,
    label: 'Other Accounts',
    description: 'Any other cash or deposit accounts',
    Icon: BankIcon,
  },
];

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const Frame186: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showManualContactModal, setShowManualContactModal] = useState(false);
  const [selectedType, setSelectedType] = useState<Account['type'] | null>(null);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [contacts, setContacts] = useState<UnifiedContact[]>([]);

  // Form fields
  const [name, setName] = useState('');
  const [institution, setInstitution] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [balance, setBalance] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [maturityDate, setMaturityDate] = useState('');
  const [beneficiaryIds, setBeneficiaryIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [documents, setDocuments] = useState<AppDocument[]>([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

  useEffect(() => {
    loadContacts();

    if (session?.taskProgress?.financial_assets?.answers) {
      const savedAccounts = session.taskProgress.financial_assets.answers.cashAccounts || [];
      if (savedAccounts.length > 0) {
        const normalizedAccounts = savedAccounts.map((account: Account) => ({
          ...account,
          documents: normalizeDocuments(account.documents || []),
        }));

        console.log('[Frame186] Loading cash accounts from session:', normalizedAccounts);
        setAccounts(normalizedAccounts);
        return;
      }
    }

    try {
      const backupData = localStorage.getItem('cash-accounts-backup');
      if (backupData) {
        const data = JSON.parse(backupData);
        if (data.length > 0) {
          const normalizedAccounts = data.map((account: Account) => ({
            ...account,
            documents: normalizeDocuments(account.documents || []),
          }));

          console.log('[Frame186] Loading accounts from localStorage backup:', normalizedAccounts);
          setAccounts(normalizedAccounts);
        }
      }
    } catch (error) {
      console.error('[Frame186] Failed to load from backup:', error);
    }
  }, [session?.taskProgress?.financial_assets]);

  const loadContacts = async () => {
    try {
      const allContacts = await unifiedContactService.getAllContacts();
      setContacts(allContacts);
      console.log(`[Frame186] Loaded ${allContacts.length} contacts for beneficiary selection`);
    } catch (error) {
      console.error('[Frame186] Error loading contacts:', error);
    }
  };

  const handleManualContactSubmit = async (contactData: { firstName: string; lastName: string; email?: string; phone?: string }) => {
    const newContact = await unifiedContactService.createContact({
      firstName: contactData.firstName.trim(),
      lastName: contactData.lastName.trim(),
      email: contactData.email?.trim() || '',
      phone: contactData.phone?.trim(),
      relationship: 'beneficiary',
      source: 'manual',
      notes: 'Added during beneficiary assignment'
    });

    const allContacts = await unifiedContactService.getAllContacts(true);
    setContacts(allContacts);

    setBeneficiaryIds(prev => {
      const firstEmptyIndex = prev.findIndex(id => !id);
      if (firstEmptyIndex >= 0) {
        const updated = [...prev];
        updated[firstEmptyIndex] = newContact.id;
        return updated;
      }
      return [...prev, newContact.id];
    });

    setShowManualContactModal(false);
  };

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame186] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const resetForm = () => {
    setName('');
    setInstitution('');
    setAccountNumber('');
    setRoutingNumber('');
    setBalance('');
    setInterestRate('');
    setMaturityDate('');
    setBeneficiaryIds([]);
    setNotes('');
    setDocuments([]);
    setEditingAccountId(null);
    setIsUploadingDocuments(false);
  };

  const handleOpenModal = (type: Account['type']) => {
    resetForm();
    setSelectedType(type);
    setShowModal(true);
  };

  const handleEditAccount = (account: Account) => {
    setSelectedType(account.type);
    setName(account.name);
    setInstitution(account.institution);
    setAccountNumber(account.accountNumber);
    setRoutingNumber(account.routingNumber || '');
    setBalance(account.balance?.toString() || '');
    setInterestRate(account.interestRate?.toString() || '');
    setMaturityDate(account.maturityDate || '');
    setBeneficiaryIds(account.beneficiaries?.map((b) => b.contactId) || []);
    setNotes(account.notes || '');
    setEditingAccountId(account.id);
    setDocuments(normalizeDocuments(account.documents || []));
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!selectedType || !name || !institution || !accountNumber) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const beneficiaryData = beneficiaryIds
        .map((id) => {
          const contact = contacts.find((c) => c.id === id);
          return contact
            ? {
                contactId: id,
                name: `${contact.firstName} ${contact.lastName}`,
              }
            : null;
        })
        .filter((b): b is Beneficiary => b !== null);

      const accountData: Account = {
        id: editingAccountId || Date.now().toString(),
        type: selectedType,
        name: name.trim(),
        institution: institution.trim(),
        accountNumber: accountNumber.trim(),
        routingNumber: routingNumber.trim() || undefined,
        balance: balance ? parseFloat(balance.replace(/,/g, '')) : 0,
        interestRate: interestRate ? parseFloat(interestRate) : undefined,
        maturityDate: maturityDate || undefined,
        beneficiaries: beneficiaryData.length > 0 ? beneficiaryData : undefined,
        notes: notes.trim() || undefined,
        documents,
      };

      const updatedAccounts = editingAccountId
        ? accounts.map((a) => (a.id === editingAccountId ? accountData : a))
        : [...accounts, accountData];

      try {
        localStorage.setItem('cash-accounts-backup', JSON.stringify(updatedAccounts));
        localStorage.setItem(
          'cash-accounts-data-backup',
          JSON.stringify({
            accounts: updatedAccounts,
            lastUpdated: new Date().toISOString(),
          })
        );
      } catch (error) {
        console.error('[Frame186] Failed to save to backup:', error);
      }

      // Sync distribution record: recalculate equal-split percentages for all
      // current beneficiaries. Runs for both new and edited accounts.
      const currentDist = (session.taskProgress?.assetDistribution?.answers?.distributions ?? {}) as DistributionMap;
      const syncedDist = autoSyncDistributionForAsset(currentDist, accountData.id, beneficiaryData);
      await updateTaskProgress('assetDistribution', { distributions: syncedDist });

      await updateTaskProgress('financial_assets', { cashAccounts: updatedAccounts });
      setAccounts(updatedAccounts);
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('[Frame186] Failed to save account:', error);
      alert('Failed to save account. Please try again.');
    }
  };

  const handleDeleteAccount = async (accountId: string) => {
    if (!confirm('Are you sure you want to delete this account?')) return;

    const accountToDelete = accounts.find((a) => a.id === accountId);
    const attachedDocs = accountToDelete?.documents || [];

    for (const doc of attachedDocs) {
      try {
        await documentService.delete(doc.id);
      } catch (error) {
        console.error('[Frame186] Failed to delete document while deleting account:', error);
      }
    }

    const updatedAccounts = accounts.filter((a) => a.id !== accountId);

    try {
      localStorage.setItem('cash-accounts-backup', JSON.stringify(updatedAccounts));
      localStorage.setItem(
        'cash-accounts-data-backup',
        JSON.stringify({
          accounts: updatedAccounts,
          lastUpdated: new Date().toISOString(),
        })
      );
    } catch (error) {
      console.error('[Frame186] Failed to save to backup:', error);
    }

    // Cascade: remove distribution record for the deleted account.
    const currentDist = (session.taskProgress?.assetDistribution?.answers?.distributions ?? {}) as DistributionMap;
    if (currentDist[accountId]) {
      const cleanedDist = removeAssetDistribution(currentDist, accountId);
      await updateTaskProgress('assetDistribution', { distributions: cleanedDist });
    }

    await updateTaskProgress('financial_assets', { cashAccounts: updatedAccounts });
    setAccounts(updatedAccounts);
  };

  const handleNext = async () => {
    navigate('/frame-187');
  };

  const handleBack = () => {
    navigate('/frame-174');
  };

  const removeDocumentFromForm = async (index: number) => {
    const docToRemove = documents[index];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame186] Failed to delete unsaved document:', error);
      alert('Failed to delete document.');
      return;
    }

    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveSavedDocument = async (accountId: string, documentIndex: number) => {
    const account = accounts.find((a) => a.id === accountId);
    const docToRemove = account?.documents?.[documentIndex];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame186] Failed to delete saved document:', error);
      alert('Failed to delete document.');
      return;
    }

    const updatedAccounts = accounts.map((a) => {
      if (a.id !== accountId) return a;

      return {
        ...a,
        documents: normalizeDocuments(a.documents || []).filter((_, i) => i !== documentIndex),
      };
    });

    setAccounts(updatedAccounts);
    await updateTaskProgress('financial_assets', {
      cashAccounts: updatedAccounts,
    });

    try {
      localStorage.setItem('cash-accounts-backup', JSON.stringify(updatedAccounts));
      localStorage.setItem(
        'cash-accounts-data-backup',
        JSON.stringify({
          accounts: updatedAccounts,
          lastUpdated: new Date().toISOString(),
        })
      );
    } catch (e) {
      console.error('[Frame186] Failed to update backup after doc removal', e);
    }
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader title="Cash and Deposit Accounts" />
        <SubHeadline className="mb-6">
          Add your checking, savings, money market accounts, and certificates of deposit. This
          information will help your loved ones locate and manage these accounts.
        </SubHeadline>

        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-lg p-4 mb-8">
          <h3 className="text-sm font-semibold text-teal-800 mb-2">Important Notes</h3>
          <ul className="text-sm text-teal-700 space-y-1">
            <li>• Account balances fluctuate regularly.</li>
            <li>
              • Your executor can confirm balances directly with the bank using a death
              certificate and proof of authority.
            </li>
          </ul>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {ACCOUNT_TYPES.map((type) => (
            <button
              key={type.key}
              onClick={() => handleOpenModal(type.key)}
              className="p-6 bg-white rounded-lg border border-stone-200 hover:border-cyan-500 transition-colors text-left"
            >
              <div className="flex items-center gap-4">
                <type.Icon className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-stone-800 leading-tight">{type.label}</h3>
                  <p className="text-sm text-stone-600 mt-1">{type.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {accounts.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-medium text-stone-800 mb-4">
              Your Accounts ({accounts.length})
            </h2>

            <div
              className={`space-y-4 ${accounts.length >= 3 ? 'max-h-56 min-h-0 overflow-y-auto' : ''}`}
            >
              {accounts.map((account) => {
                const accountType = ACCOUNT_TYPES.find((type) => type.key === account.type);
                const AccountIcon = accountType?.Icon || BankIcon;
                const activeBeneficiaries = (account.beneficiaries || []).filter((b) =>
                  contacts.some((c) => c.id === b.contactId && c.active !== false)
                );

                return (
                  <div
                    key={account.id}
                    className="bg-white rounded-lg border border-neutral-200 p-4"
                  > 
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex flex-col gap-2 min-w-0 flex-1 sm:flex-row sm:items-start sm:gap-4">
                        <AccountIcon className="w-6 h-6 text-cyan-700 flex-shrink-0" />

                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-stone-800 break-words">{account.name}</div>
                          <div className="text-sm text-stone-500 mt-1 break-words">
                            {account.institution}
                            {account.accountNumber &&
                              ` • Account ending in ${account.accountNumber.slice(-4)}`}
                            {account.balance !== undefined &&
                              ` • Balance: $${account.balance.toLocaleString()}`}
                            {account.interestRate !== undefined &&
                              ` • Interest Rate: ${account.interestRate}%`}
                          </div>

                          {activeBeneficiaries.length > 0 && (
                            <div className="text-sm text-stone-600 mt-2 break-words">
                              Beneficiaries: {activeBeneficiaries.map((b) => b.name).join(', ')}
                            </div>
                          )}

                          {account.notes && (
                            <div className="text-sm text-stone-600 mt-2 break-words">{account.notes}</div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:shrink-0">
                        <button
                          onClick={() => handleEditAccount(account)}
                          className="text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded border border-cyan-200 hover:border-cyan-300"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteAccount(account.id)}
                          className="text-red-600 hover:text-red-700 px-3 py-1 rounded border border-red-200 hover:border-red-300"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {account.documents && account.documents.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <div className="text-sm text-cyan-700 font-medium">
                          {account.documents.length} document(s) attached
                        </div>

                        <div className="space-y-2">
                          {normalizeDocuments(account.documents).map((doc, index) => (
                            <div
                              key={`${account.id}-doc-${doc.id}-${index}`}
                              className="flex flex-col gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <button
                                type="button"
                                onClick={() => openDocument(doc)}
                                className="flex items-start gap-2 min-w-0 w-full text-left sm:flex-1"
                              >
                                <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                                <span
                                  className="text-sm font-medium text-stone-800 truncate"
                                  title={doc.name}
                                >
                                  {doc.name}
                                </span>
                              </button>

                              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:shrink-0">
                                <button
                                  type="button"
                                  onClick={() => downloadDocument(doc)}
                                  className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                                >
                                  Download
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleRemoveSavedDocument(account.id, index)}
                                  className="text-red-600 hover:text-red-700 text-xs font-medium"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </ResponsiveContent>

      <ResponsiveFooter
        title="Inventory Your Assets"
        subtitle="Add your cash and deposit accounts"
        progressPercent={12.5}
        onNext={handleNext}
        onBack={handleBack}
        nextText="Next"
        backText="Back"
      />

      <Modal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editingAccountId ? 'Edit Account' : 'Add Account'}
        okText={editingAccountId ? 'Update Account' : 'Add Account'}
        cancelText="Cancel"
        onOk={handleSave}
        onCancel={() => {
          setShowModal(false);
          resetForm();
        }}

      >
        <div className="space-y-6">

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Account Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="e.g., Primary Checking"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Financial Institution *
            </label>
            <input
              type="text"
              value={institution}
              onChange={(e) => setInstitution(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="e.g., Bank of America"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Account Number *
            </label>
            <input
              type="text"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="Account number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Current Balance (optional)
            </label>
            <input
              type="text"
              value={balance}
              onChange={(e) => setBalance(e.target.value.replace(/[^0-9.]/g, ''))}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="0.00"
            />
          </div>

          {selectedType === 'cd' && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Maturity Date
              </label>
              <input
                type="date"
                value={maturityDate}
                onChange={(e) => setMaturityDate(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Beneficiaries
            </label>
            <p className="text-xs text-stone-600 mb-2">
              <strong>Note:</strong> Beneficiary designations are governed by the financial
              institution. Please make sure the information here matches what&apos;s on file.
            </p>

            <div className="space-y-2">
              {beneficiaryIds.map((beneficiaryId, index) => (
                <div key={index} className="flex items-center gap-2">
                  <select
                    value={beneficiaryId}
                    onChange={(e) => {
                      if (e.target.value === '__add_new_contact__') {
                        setShowManualContactModal(true);
                        return;
                      }
                      const newBeneficiaries = [...beneficiaryIds];
                      newBeneficiaries[index] = e.target.value;
                      setBeneficiaryIds(newBeneficiaries);
                    }}
                    className="flex-1 px-3 py-2 border border-stone-300 rounded-md"
                  >
                    <option value="">Select a beneficiary</option>
                    {contacts
                      .filter((c) => c.active !== false)
                      .map((contact) => (
                        <option key={contact.id} value={contact.id}>
                          {contact.firstName} {contact.lastName}{' '}
                          {contact.relationship
                            ? `(${contact.relationship.charAt(0).toUpperCase()}${contact.relationship.slice(1)})`
                            : ''}
                        </option>
                      ))}
                    <option value="__add_new_contact__">+ Add new contact...</option>
                  </select>

                  <button
                    type="button"
                    onClick={() =>
                      setBeneficiaryIds(beneficiaryIds.filter((_, i) => i !== index))
                    }
                    className="text-sm font-medium text-[#EF4444] hover:text-red-700 ml-4"
                  >
                    Remove
                  </button>
                </div>
              ))}

              <button
                onClick={() => {
                  if (contacts.filter((c) => c.active !== false).length > 0) {
                    setBeneficiaryIds([...beneficiaryIds, '']);
                  }
                }}
                className="text-cyan-600 hover:text-cyan-700 text-sm font-medium"
              >
                + Add Beneficiary
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-stone-300 rounded-md"
              placeholder="Any additional information"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Upload Documents
            </label>

            <FileUpload
              onUpload={async (newFiles) => {
                if (!newFiles.length) return;

                setIsUploadingDocuments(true);

                try {
                  const uploadedDocs = await Promise.all(
                    newFiles.map((file) => documentService.upload(file))
                  );

                  setDocuments((prev) => [...prev, ...uploadedDocs]);
                } catch (error) {
                  console.error('[Frame186] Failed to upload document(s):', error);
                  alert('Failed to upload one or more documents.');
                } finally {
                  setIsUploadingDocuments(false);
                }
              }}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              multiple
            />

            {isUploadingDocuments && (
              <div className="mt-3 text-sm text-stone-500">
                Uploading documents...
              </div>
            )}

            {documents.length > 0 && (
              <ul className="mt-2 space-y-2">
                {documents.map((doc, index) => (
                  <li
                    key={`${doc.id}-${index}`}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm text-stone-600 bg-stone-50 p-2 border border-stone-200 rounded"
                  >
                    <button
                      type="button"
                      onClick={() => openDocument(doc)}
                      className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
                    >
                      <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                      <span className="text-cyan-700 hover:text-cyan-800 truncate" title={doc.name}>
                        {doc.name}
                      </span>
                    </button>

                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => downloadDocument(doc)}
                        className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                      >
                        Download
                      </button>

                      <button
                        onClick={() => removeDocumentFromForm(index)}
                        className="text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Modal>

      <ManualContactModal
        open={showManualContactModal}
        onClose={() => setShowManualContactModal(false)}
        onSubmit={handleManualContactSubmit}
        initialData={{ relationship: 'beneficiary' }}
      />
    </ResponsiveContainer>
  );
};

export default Frame186;